'use client';

import { c as _c } from "react/compiler-runtime";
import type { QuickFilterPreset } from '@/components/filters/QuickFilters';
import { DIRECTORY_FILTER_PARAMS } from '@/lib/directory-filters';
import { useQuickFilters, type UseQuickFiltersResult } from '@/lib/hooks/useQuickFilters';

/** Metadata key under which the Directory quick-filter presets are stored. */
export const DIRECTORY_QUICK_FILTERS_METADATA_KEY = 'directory_quick_filters';

/** Facet keys the Directory filters on (matches DIRECTORY_FILTER_PARAMS). */
const DIRECTORY_QUICK_FILTER_FACET_KEYS = Object.keys(DIRECTORY_FILTER_PARAMS) as readonly string[];

/**
 * Built-in default presets used when no `directory_quick_filters` metadata is
 * configured (or the stored value is invalid). Values are option ids from the
 * shared ROLE_OPTIONS / CAPABILITY_OPTIONS / CHANNEL_EXPERTISE_OPTIONS
 * constants and only touch facets the directory filters on.
 */
export const DEFAULT_DIRECTORY_QUICK_FILTERS: readonly QuickFilterPreset[] = [{
  id: 'retailers',
  label: 'Retailers',
  filters: {
    role: ['grocery-retailer', 'supermarket', 'other-retailer']
  }
}, {
  id: 'distribution',
  label: 'Distribution & Logistics',
  filters: {
    role: ['distributor', 'wholesaler', 'importer'],
    capability: ['distribution-setup', 'logistics-fulfilment']
  }
}, {
  id: 'marketing-activations',
  label: 'Marketing & Activations',
  filters: {
    role: ['marketing-agency', 'experiential-agency'],
    capability: ['marketing-strategy', 'activations-events', 'sampling-programs']
  }
}, {
  id: 'export-support',
  label: 'Export Support',
  filters: {
    capability: ['export-strategy', 'market-entry']
  }
}, {
  id: 'foodservice',
  label: 'Foodservice',
  filters: {
    role: ['foodservice-business', 'foodservice-supplier'],
    channelExpertise: ['foodservice']
  }
}, {
  id: 'manufacturers',
  label: 'Manufacturers & Co-packers',
  filters: {
    role: ['manufacturer', 'contract-manufacturer', 'contract-packaer']
  }
}, {
  id: 'new-product-development',
  label: 'New Product Development',
  filters: {
    role: ['manufacturer', 'contract-manufacturer'],
    capability: ['product-development', 'product-formulation', 'recipe-development', 'new-product-innovation', 'packaging-development']
  }
}];

/**
 * Retrieve the Directory quick-filter presets. Thin wrapper around the generic
 * {@link useQuickFilters} that supplies the directory metadata key, defaults and
 * facet whitelist. Pass `{ optimistic: false }` to hold off rendering until the
 * server response settles.
 */
export function useDirectoryQuickFilters(t0) {
  const $ = _c(4);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  let t2;
  if ($[2] !== options.optimistic) {
    t2 = {
      metadataKey: DIRECTORY_QUICK_FILTERS_METADATA_KEY,
      defaults: DEFAULT_DIRECTORY_QUICK_FILTERS,
      allowedFacetKeys: DIRECTORY_QUICK_FILTER_FACET_KEYS,
      optimistic: options.optimistic
    };
    $[2] = options.optimistic;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return useQuickFilters(t2);
}